import { useState, useRef, useEffect, useCallback } from "react";
import {
  API, PLANS, CURRENCIES, ISD_CODES, PRIVACY, TERMS, REFUND,
  ABOUT, CONTACT, SHIPPING, FAQ
} from "./constants";
import BlogAdmin from "./components/BlogAdmin";
import Header from "./components/Header";
import Footer from "./components/Footer";

export default function App() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("myatscore_dark");
      if (saved !== null) return saved === "true";
    } catch (e) {}
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const LIGHT = {
    bg: "#f8f9fc", bg2: "#ffffff", bg3: "#f1f3f8",
    border: "#e2e6ef", border2: "#d0d5e8",
    text: "#0f1117", text2: "#374151", text3: "#6b7280", text4: "#9ca3af",
    card: "#ffffff", cardBorder: "#e2e6ef",
    inputBg: "#f8f9fc", inputBorder: "#d0d5e8",
    headerBg: "#ffffff", headerBorder: "#e2e6ef",
    footerBg: "#f1f3f8", footerBorder: "#e2e6ef",
    tagMatchBg: "rgba(16,185,129,0.08)", tagMatchColor: "#059669", tagMatchBorder: "rgba(16,185,129,0.2)",
    tagMissBg: "rgba(239,68,68,0.08)", tagMissColor: "#dc2626", tagMissBorder: "rgba(239,68,68,0.2)",
    fixRowBorder: "#f1f3f8", shadow: "0 1px 3px rgba(0,0,0,0.08)", shadowLg: "0 10px 25px rgba(0,0,0,0.08)",
  };

  const DARK = {
    bg: "#08080f", bg2: "#0f0f1a", bg3: "#141422",
    border: "#1e1e2e", border2: "#252535",
    text: "#f0f0f8", text2: "#c4c4d4", text3: "#8888a8", text4: "#55556a",
    card: "#0f0f1a", cardBorder: "#1e1e2e",
    inputBg: "#0a0a14", inputBorder: "#1e1e2e",
    headerBg: "#0a0a12", headerBorder: "#141422",
    footerBg: "#08080f", footerBorder: "#141422",
    tagMatchBg: "rgba(0,217,126,0.1)", tagMatchColor: "#00d97e", tagMatchBorder: "rgba(0,217,126,0.2)",
    tagMissBg: "rgba(255,77,109,0.1)", tagMissColor: "#ff4d6d", tagMissBorder: "rgba(255,77,109,0.2)",
    fixRowBorder: "#141422", shadow: "none", shadowLg: "0 10px 25px rgba(0,0,0,0.3)",
  };

  useEffect(() => {
    localStorage.setItem("myatscore_dark", dark);
    if (dark) {
      document.body.classList.add("dark");
      document.body.classList.remove("light");
    } else {
      document.body.classList.add("light");
      document.body.classList.remove("dark");
    }
  }, [dark]);

  const [page, setPage] = useState("main");
  const [step, setStep] = useState("landing"); // landing | pricing | activate | input | result
  const [resume, setResume] = useState("");
  const [jd, setJd] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activateTab, setActivateTab] = useState("pay"); // pay | login

  // ── CURRENCY ─────────────────────────────────────────────────────────────
  const [currency, setCurrency] = useState("INR");
  const [rates, setRates] = useState({});
  const [currencyLoading, setCurrencyLoading] = useState(true);

  useEffect(() => {
    const extractRates = (r) => ({
      USD: r.USD, GBP: r.GBP, EUR: r.EUR, AED: r.AED,
      SGD: r.SGD, AUD: r.AUD, CAD: r.CAD, QAR: r.QAR, SAR: r.SAR,
    });

    const loadRates = async () => {
      try {
        const res1 = await fetch("https://open.er-api.com/v6/latest/INR");
        const data1 = await res1.json();
        if (data1?.rates?.USD) {
          setRates(extractRates(data1.rates));
          return;
        }
      } catch (e) {}

      try {
        const res2 = await fetch("https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/inr.json");
        const data2 = await res2.json();
        if (data2?.inr?.usd) {
          const r = data2.inr;
          setRates({
            USD: r.usd, GBP: r.gbp, EUR: r.eur, AED: r.aed,
            SGD: r.sgd, AUD: r.aud, CAD: r.cad, QAR: r.qar, SAR: r.sar,
          });
          return;
        }
      } catch (e) {}

      try {
        const res3 = await fetch("https://api.frankfurter.app/latest?from=INR&to=USD,GBP,EUR,SGD,AUD,CAD");
        const data3 = await res3.json();
        if (data3?.rates?.USD) {
          setRates(data3.rates);
        }
      } catch (e) {}
    };

    loadRates().finally(() => setCurrencyLoading(false));

    // Auto-detect country — independent of rates fetch
    fetch("https://ipapi.co/json/")
      .then(r => r.json())
      .then(geo => {
        const countryToCurrency = {
          US: "USD", GB: "GBP", AE: "AED", SG: "SGD",
          AU: "AUD", CA: "CAD", QA: "QAR", SA: "SAR",
          DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR",
          IN: "INR",
        };
        const detected = countryToCurrency[geo.country_code] || "INR";
        setCurrency(detected);
      })
      .catch(() => {})
      .finally(() => setCurrencyLoading(false));
  }, []);

  const fmtPrice = (inrAmount) => {
    if (currency === "INR") {
      return "₹" + inrAmount.toLocaleString("en-IN");
    }
    const rate = rates[currency];
    if (!rate) {
      // Rates not loaded yet — show INR while loading.
      return "₹" + inrAmount.toLocaleString("en-IN");
    }
    const converted = inrAmount * rate;
    const sym = CURRENCIES.find(c => c.code === currency)?.symbol || currency;
    const formatted = converted < 10
      ? converted.toFixed(2)
      : Math.round(converted).toLocaleString("en-US");
    return sym + " " + formatted;
  };

  const fmtPerAnalysis = (inrAmount) => {
    return fmtPrice(inrAmount) + " per analysis";
  };
  const [copiedId, setCopiedId] = useState(null);
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const [credits, setCredits] = useState(0);
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState(""); // google | facebook
  const [paymentMethod, setPaymentMethod] = useState("razorpay"); // razorpay | ccavenue (ccavenue temporarily disabled)
  const [code, setCode] = useState("");  const [accessKey, setAccessKey] = useState("");
  const [activationError, setActivationError] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);

  // ─── SEO Management ───
  useEffect(() => {
    let title = "MyATScore — ATS Resume Checker & Score Optimizer";
    let desc = "Free ATS resume checker for Workday, Greenhouse, Taleo & Naukri. Get your ATS score, missing keywords, and an AI-rewritten summary in 15 seconds. Pay per use.";
    
    if (page === "blog") {
      title = "Resources & Career Insights — MyATScore Blog";
      desc = "Expert advice on beating the ATS, resume optimization, and landing your dream job.";
    } else if (page === "blog-post" && selectedBlogPost) {
      title = `${selectedBlogPost.title} — MyATScore Blog`;
      desc = selectedBlogPost.excerpt;
    } else if (page !== "main") {
      const pageTitle = page.charAt(0).toUpperCase() + page.slice(1).replace("-", " ");
      title = `${pageTitle} — MyATScore`;
    }

    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
    
    // Update OpenGraph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", desc);

    // Canonical tag
    let canonical = "https://myatscore.com";
    if (page === "blog") canonical += "/blog";
    else if (page === "blog-post" && selectedBlogPost) canonical += `/blog/${selectedBlogPost.id}`;
    else if (page !== "main") canonical += `/${page}`;
    
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) canonicalTag.setAttribute("href", canonical);

    // Breadcrumb Schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://myatscore.com" }
      ]
    };
    if (page === "blog") {
      breadcrumbSchema.itemListElement.push({ "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://myatscore.com/blog" });
    } else if (page === "blog-post" && selectedBlogPost) {
      breadcrumbSchema.itemListElement.push({ "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://myatscore.com/blog" });
      breadcrumbSchema.itemListElement.push({ "@type": "ListItem", "position": 3, "name": selectedBlogPost.title, "item": `https://myatscore.com/blog/${selectedBlogPost.id}` });
    } else if (page !== "main") {
      breadcrumbSchema.itemListElement.push({ "@type": "ListItem", "position": 2, "name": page.charAt(0).toUpperCase() + page.slice(1), "item": `https://myatscore.com/${page}` });
    }

    let schemaTag = document.getElementById("breadcrumb-schema");
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.id = "breadcrumb-schema";
      schemaTag.type = "application/ld+json";
      document.head.appendChild(schemaTag);
    }
    schemaTag.text = JSON.stringify(breadcrumbSchema);
  }, [page, selectedBlogPost]);
  const topRef = useRef(null);
  const resultRef = useRef(null);

  const t = dark ? DARK : LIGHT;
  const accent = "#6d28d9";
  const accentLight = dark ? "rgba(109,40,217,0.15)" : "rgba(109,40,217,0.08)";
  const accentBorder = dark ? "rgba(109,40,217,0.3)" : "rgba(109,40,217,0.2)";
  const [paying, setPaying] = useState(false);

  // Fetch blog posts on mount
  useEffect(() => {
    fetch(`${API}/api/blog`)
      .then(res => res.json())
      .then(data => setBlogPosts(data))
      .catch(err => console.error("Failed to fetch blog posts", err));
  }, []);

  const [discordId, setDiscordId] = useState("");
  const [billingName, setBillingName] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [razorpayPhone, setRazorpayPhone] = useState("");
  const [razorpayIsd, setRazorpayIsd] = useState("+91");

  const [billingAddress, setBillingAddress] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingCountry, setBillingCountry] = useState("India");

  const googleBtnHeroRef = useRef(null);
  const googleBtnLoginRef = useRef(null);

  const scrollTop = useCallback(() => setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 80), []);

  const handleSocialLogin = useCallback(async (provider, token) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/social-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, token }),
      });
      const data = await res.json();
      if (!data.success && !data.valid) throw new Error(data.error || "Social login failed.");
      
      // Update state with returned credentials
      setCode(data.code);
      setAccessKey(data.accessKey);
      setCredits(data.credits);
      setProvider(provider);
      const matchedPlan = PLANS.find(p => p.name === data.plan);
      if (matchedPlan) setSelectedPlan(matchedPlan);
      
      setStep("input");
      scrollTop();
    } catch (err) {
      setError(err.message);
      setStep("activate");
    } finally {
      setLoading(false);
    }
  }, [scrollTop]);

  const handleGoogleLogin = useCallback((response) => {
    if (response.credential) {
      handleSocialLogin("google", response.credential);
    }
  }, [handleSocialLogin]);

  // Load Google Identity Services SDK and initialize
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");

  useEffect(() => {
    const loadGoogle = async () => {
      try {
        const configRes = await fetch(`${API}/api/config`);
        const config = await configRes.json();
        const clientId = config.googleClientId;
        if (!clientId || clientId.includes("YOUR_GOOGLE")) return;
        setGoogleClientId(clientId);

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => setGoogleLoaded(true);
        document.body.appendChild(script);
      } catch (err) {
        console.error("Failed to load Google config", err);
      }
    };
    loadGoogle();
  }, []);

  useEffect(() => {
    if (googleLoaded && googleClientId && window.google) {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleLogin,
      });

      const render = () => {
        [googleBtnHeroRef, googleBtnLoginRef].forEach(ref => {
          if (ref.current) {
            window.google.accounts.id.renderButton(ref.current, {
              theme: dark ? "filled_black" : "outline",
              size: "large",
              shape: "rectangular",
              text: "signin_with",
              width: 200
            });
          }
        });
      };

      render();
      // Delay slightly to ensure React has finished DOM updates if refs were just created
      const timer = setTimeout(render, 100);
      return () => clearTimeout(timer);
    }
  }, [googleLoaded, googleClientId, handleGoogleLogin, dark, step, activateTab, page, selectedPlan]);

  const initAutocomplete = useCallback(() => {
    if (!addressInputRef.current || !window.google) return;
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "in" },
    });
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;
      let street = "", city = "", state = "", zip = "";
      for (const comp of place.address_components) {
        const types = comp.types;
        if (types.includes("street_number")) street = comp.long_name + " " + street;
        if (types.includes("route")) street = street + comp.long_name;
        if (types.includes("sublocality_level_1") || types.includes("locality")) city = comp.long_name;
        if (types.includes("administrative_area_level_1")) state = comp.long_name;
        if (types.includes("postal_code")) zip = comp.long_name;
      }
      setBillingAddress(street.trim() || place.formatted_address || "");
      if (city) setBillingCity(city);
      if (zip) setBillingZip(zip);
      if (state) setBillingState(state);
      // Auto-fill country from Google Maps
      const countryComp = place.address_components.find(c => c.types.includes("country"));
      if (countryComp) setBillingCountry(countryComp.long_name);
    });
  }, []);

  // Google Places Autocomplete
  const addressInputRef = useRef(null);
  useEffect(() => {
    if (paymentMethod !== "ccavenue") return;
    const GOOGLE_API_KEY = "AIzaSyCFiPgK92CoaRGNPlitHVTGlMKt40tPXQo";
    const scriptId = "google-places-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => initAutocomplete();
      document.body.appendChild(script);
    } else if (window.google) {
      initAutocomplete();
    }
  }, [paymentMethod, initAutocomplete]);

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // Auto-fill email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem("myatscore_email");
    if (savedEmail) setEmail(savedEmail);
  }, []);

  // Save email to localStorage when it changes
  useEffect(() => {
    if (email) localStorage.setItem("myatscore_email", email);
  }, [email]);

  // Detect URL parameters (Payment returns or deep links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const reason = params.get("reason");
    const pageParam = params.get("page");

    if (pageParam === "admin") {
      setPage("admin");
      window.history.replaceState({}, "", "/"); // Clean URL after loading
      return;
    }

    if (payment === "success") {
      // Restore saved plan from localStorage
      const savedPlan = localStorage.getItem("myatscore_pending_plan");
      if (savedPlan) {
        const plan = PLANS.find(p => p.id === savedPlan);
        if (plan) setSelectedPlan(plan);
      }
      setPaymentSuccess(true);
      setStep("activate");
      localStorage.removeItem("myatscore_pending_plan");
      window.history.replaceState({}, "", "/");
    } else if (payment === "cancelled") {
      setPaymentError("Payment was cancelled. You can try again.");
      setStep("pricing");
      localStorage.removeItem("myatscore_pending_plan");
      window.history.replaceState({}, "", "/");
    } else if (payment === "failed") {
      setPaymentError(reason || "Payment failed. Please try again or contact support@myatscore.com");
      setStep("pricing");
      localStorage.removeItem("myatscore_pending_plan");
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const handleCCAvenuePayment = async () => {
    if (!billingName.trim() || !billingPhone.trim() || !billingAddress.trim() || !billingCity.trim() || !billingZip.trim() || !billingState.trim() || !billingCountry.trim()) {
      alert("Please fill in all billing details before proceeding.");
      setPaying(false);
      return;
    }
    try {
      // Save plan so we can restore after redirect
      localStorage.setItem("myatscore_pending_plan", selectedPlan.id);
      const res = await fetch(`${API}/api/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: selectedPlan.id,
          email,
          billingName: billingName.trim(),
          billingPhone: billingPhone.trim(),
          billingAddress: billingAddress.trim(),
          billingCity: billingCity.trim(),
          billingZip: billingZip.trim(),
          billingState: billingState.trim(),
          billingCountry: billingCountry.trim(),
          ...(discordId.trim() && { discord_id: discordId.trim() }),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = "https://secure.ccavenue.com/transaction/transaction.do?command=initiateTransaction";
      [["encRequest", data.encRequest], ["access_code", data.accessCode]].forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden"; input.name = k; input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setPaymentError(err.message || "CCAvenue payment setup failed. Please try again.");
      setPaying(false);
    }
  };

  const handleRazorpayPayment = async () => {

    try {
      const res = await fetch(`${API}/api/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: selectedPlan.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "MyATScore",
        description: `Activation Code for ${selectedPlan.name} Plan`,
        order_id: data.id,
        handler: async (response) => {
          setPaying(true);
          try {
            const verifyRes = await fetch(`${API}/api/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...response,
                planId: selectedPlan.id,
                email: email
              }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              setCode(verifyData.code);
              setAccessKey(verifyData.accessKey);
              setStep("activate");
              alert("Payment successful! Your activation code and key have been filled. Please click Unlock Credits to start.");
            } else {
              throw new Error(verifyData.error);
            }
          } catch (err) {
            setPaymentError(err.message || "Payment verification failed. Please contact support@myatscore.com.");
          } finally {
            setPaying(false);
          }
        },
        prefill: { email: email, contact: razorpayPhone.trim() ? razorpayIsd + razorpayPhone.trim() : "" },
        notes: { plan_id: selectedPlan.id, ...(discordId.trim() && { discord_id: discordId.trim() }) },
        theme: { color: accent },
      };

      // Reset button + show banner if user closes/cancels the Razorpay modal
      options.modal = {
        ondismiss: () => {
          setPaying(false);
          setPaymentError("Payment was cancelled. You can try again.");
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (err) => {
        const reason = err?.error?.description || err?.error?.reason || "Payment was declined. Please try a different payment method or contact your bank.";
        setPaymentError(reason);
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      setPaymentError(err.message || "Payment setup failed. Please try again.");
      setPaying(false);
    }
  };

  const handlePayment = async () => {
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address to receive your activation code.");
      return;
    }
    if (!paymentMethod) {
      setPaymentError("Please select a payment method to continue.");
      setPaying(false);
      return;
    }
    setPaying(true);
    if (paymentMethod === "razorpay") {
      await handleRazorpayPayment();
    } else {
      await handleCCAvenuePayment();
    }
  };

  const goPage = (p) => { setPage(p); scrollTop(); };
  const selectPlan = (plan) => { setSelectedPlan(plan); setActivateTab("pay"); setStep("activate"); scrollTop(); };
  const changePlan = () => { setStep("pricing"); setResult(null); setResume(""); setJd(""); setError(""); setSelectedPlan(null); setCredits(0); setCode(""); setAccessKey(""); scrollTop(); };

  const handleLogout = useCallback(() => {
    window.google?.accounts?.id?.disableAutoSelect();
    setCode(""); setAccessKey(""); setCredits(0); setProvider(""); setSelectedPlan(null);
    setResult(null); setResume(""); setJd(""); setError("");
    setStep("landing"); scrollTop();
  }, [scrollTop]);

  const activate = async () => {
    setActivationError("");
    if (!code.trim() || !accessKey.trim()) { setActivationError("Please enter both your activation code and access key."); return; }
    try {
      const res = await fetch(`${API}/api/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim(), accessKey: accessKey.trim(), provider }),
      });
      const data = await res.json();
      if (!data.valid) { setActivationError(data.error || "Invalid code or key. Please check and try again."); return; }
      setCredits(data.credits);
      const matchedPlan = PLANS.find(p => p.name === data.plan);
      if (matchedPlan) setSelectedPlan(matchedPlan);
      setStep("input"); scrollTop();
    } catch {
      setActivationError("Connection error. Please try again.");
    }
  };

  const analyze = async () => {
    if (!resume.trim() || !jd.trim()) { setError("Please paste both your resume and the job description."); return; }
    if (credits <= 0) { setError("Credits exhausted. Please get more credits to perform another analysis."); return; }
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API}/api/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim(), accessKey: accessKey.trim(), resume, jd }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Analysis failed. Please try again.");
      setResult(data.result);
      setCredits(data.creditsRemaining);
      setStep("result");
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const analyzeAnother = () => { setStep("input"); setResult(null); setResume(""); setJd(""); setError(""); scrollTop(); };

  const [showRestore, setShowRestore] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [restoreStatus, setRestoreStatus] = useState(null);
  const [resending, setResending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleResendCode = async () => {
    if (!restoreEmail.trim()) return;
    setResending(true);
    setRestoreStatus(null);
    try {
      const res = await fetch(`${API}/api/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: restoreEmail.trim() }),
      });
      const data = await res.json();
      setRestoreStatus(data.success ? "sent" : "error");
    } catch {
      setRestoreStatus("error");
    } finally {
      setResending(false);
    }
  };

  const handleSavePDF = () => {
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow popups for myatscore.com to save your PDF. Click the popup blocked icon in your browser's address bar."); return; }
    const scoreColor = result ? (result.ats_score >= 75 ? "#059669" : result.ats_score >= 50 ? "#d97706" : "#dc2626") : "#111";
    const matchedHTML = (result?.matched_keywords || []).map(k => `<span class="tag match">${k}</span>`).join(" ");
    const missingHTML = (result?.missing_keywords || []).map(k => `<span class="tag miss">${k}</span>`).join(" ");
    const fixesHTML = (result?.critical_fixes || []).map((f, i) => `<div class="fix">${i + 1}. ${f}</div>`).join("");
    const strengthsHTML = (result?.strengths || []).map(s => `<div class="fix">• ${s}</div>`).join("");
    const quickWinsHTML = (result?.quick_wins || []).map(q => `<div class="fix">• ${q}</div>`).join("");
    const breakdownHTML = result?.score_breakdown ? [
      { label: "Keyword Match", key: "keyword_match", max: 30 },
      { label: "Experience Relevance", key: "experience_relevance", max: 25 },
      { label: "Section Completeness", key: "section_completeness", max: 20 },
      { label: "ATS Formatting", key: "formatting_ats_friendliness", max: 15 },
      { label: "Language & Impact", key: "language_impact", max: 10 },
    ].map(({ label, key, max }) => {
      const val = result.score_breakdown[key] ?? 0;
      const pct = Math.round((val / max) * 100);
      return `<div style="margin-bottom:8px"><div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:3px"><span>${label}</span><span style="font-weight:700">${val}/${max}</span></div><div style="background:#e5e7eb;border-radius:4px;height:8px"><div style="background:${pct>=70?"#059669":pct>=40?"#d97706":"#dc2626"};width:${pct}%;height:8px;border-radius:4px"></div></div></div>`;
    }).join("") : "";
    const sectionCheckHTML = result?.section_check ? Object.entries(result.section_check).map(([k, v]) => {
      const label = k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      return `<span style="display:inline-block;margin:3px 6px 3px 0;font-size:12px">${v ? "✓" : "✗"} ${label}</span>`;
    }).join("") : "";
    const bulletsHTML = (result?.bullet_rewrites || []).map(rw => `
      <div style="margin-bottom:14px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">
        <div style="background:#fee2e2;padding:8px 12px;font-size:11px;font-weight:700;color:#dc2626">BEFORE</div>
        <div style="padding:8px 12px;font-size:13px">${rw.original}</div>
        <div style="background:#dcfce7;padding:8px 12px;font-size:11px;font-weight:700;color:#059669">AFTER</div>
        <div style="padding:8px 12px;font-size:13px">${rw.improved}</div>
      </div>`).join("");
    win.document.write(`<!DOCTYPE html><html><head>
      <title>MyATScore Results — ${new Date().toLocaleDateString("en-IN")}</title>
      <style>
        body{font-family:Arial,sans-serif;max-width:700px;margin:32px auto;color:#111;font-size:14px;line-height:1.6}
        h1{font-size:22px;color:#6d28d9;margin-bottom:4px}
        h2{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b7280;margin:24px 0 8px}
        .score{font-size:52px;font-weight:800;color:${scoreColor};margin:4px 0}
        .tag{display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600;margin:2px}
        .match{background:#dcfce7;color:#059669}.miss{background:#fee2e2;color:#dc2626}
        .fix{padding:6px 0;border-bottom:1px solid #f1f3f8;font-size:13px}
        .summary{background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:14px;font-style:italic;font-size:14px}
        .footer{margin-top:32px;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px}
      </style>
    </head><body>
      <h1>MyATScore Analysis Results</h1>
      <p style="color:#6b7280;margin:2px 0">Generated on ${new Date().toLocaleDateString("en-IN")} · myatscore.com</p>
      <div class="score">${result?.ats_score}/100</div>
      <p style="color:#374151">${result?.verdict || ""}</p>
      ${breakdownHTML ? `<h2>📊 Score Breakdown</h2>${breakdownHTML}` : ""}
      ${sectionCheckHTML ? `<h2>📋 Resume Section Check</h2>${sectionCheckHTML}` : ""}
      <h2>✓ Matched Keywords</h2>${matchedHTML}
      <h2>✗ Missing Keywords</h2>${missingHTML}
      ${fixesHTML ? `<h2>🔧 Critical Fixes</h2>${fixesHTML}` : ""}
      ${strengthsHTML ? `<h2>💪 Strengths</h2>${strengthsHTML}` : ""}
      ${quickWinsHTML ? `<h2>🎯 Quick Wins</h2>${quickWinsHTML}` : ""}
      ${bulletsHTML ? `<h2>✏️ Bullet Point Rewrites</h2>${bulletsHTML}` : ""}
      ${result?.rewrite_summary ? `<h2>✨ Optimised Professional Summary</h2><div class="summary">${result.rewrite_summary}</div>` : ""}
      <div class="footer">Generated by MyATScore · myatscore.com · Credits valid for 12 months of activity</div>
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  const [freeResume, setFreeResume] = useState("");
  const [freeJd, setFreeJd] = useState("");
  const [freeResult, setFreeResult] = useState(null);

  const runFreeCheck = () => {
    if (!freeResume.trim() || !freeJd.trim()) return;


    // Master list of tech skills/phrases to look for in JD and resume
    const techSkills = [
      // BI & Visualization
      "power bi","power query","power automate","tableau","qlik sense","qlik view","looker","looker studio","metabase","superset","spotfire","microstrategy","ssrs","crystal reports",
      // Data & Cloud
      "azure sql","azure data factory","azure synapse","azure databricks","aws redshift","google bigquery","snowflake","databricks","spark","hadoop","hive","dbt","fivetran","airflow",
      "sql server","mysql","postgresql","oracle","mongodb","cassandra","dynamodb","cosmos db",
      // Languages & Libraries
      "python","scala","java","r programming","julia","pyspark","pandas","numpy","scikit-learn","tensorflow","pytorch","keras","matplotlib","seaborn","plotly",
      // BI Features
      "dax","power query m","rls","row level security","drill through","drill-through","bookmarks","custom visuals","paginated reports","dataflows","composite models","incremental refresh","embedded analytics","sharepoint","teams integration",
      // Data Concepts
      "data warehouse","data warehousing","data modeling","data modelling","data pipeline","data engineering","data science","data analytics","data visualization","data transformation","data integration","data governance","data quality","data lake","data lakehouse","data mesh","etl","elt","star schema","snowflake schema","slowly changing dimension","fact table","dimension table",
      // AI/ML
      "machine learning","deep learning","artificial intelligence","generative ai","large language model","llm","nlp","natural language processing","computer vision","ai agents","reinforcement learning","mlops","feature engineering",
      // Other Tech
      "excel","vba","sharepoint","power apps","rest api","graphql","git","github","docker","kubernetes","jenkins","ci/cd","agile","scrum","kanban","jira","confluence",
      // Soft/Domain
      "business intelligence","project management","stakeholder management","cross functional","data driven","kpi","okr","data storytelling","executive dashboard","self service",
    ];

    const jdLower = freeJd.toLowerCase();
    const resumeLower = freeResume.toLowerCase();

    // Only keep tech skills that appear in the JD using word boundaries
    const jdKeywords = techSkills.filter(skill => {
      const regex = new RegExp(`\\b${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return regex.test(jdLower);
    });
    const matched = jdKeywords.filter(skill => {
      const regex = new RegExp(`\\b${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return regex.test(resumeLower);
    });
    const missing = jdKeywords.filter(skill => !matched.includes(skill));

    const matchRate = matched.length / Math.max(jdKeywords.length, 1);
    const estimatedScore = Math.min(Math.max(Math.round(matchRate * 85) + 10, 10), 92);

    setFreeResult({
      score: estimatedScore,
      matched: matched.slice(0, 8),
      totalMatched: matched.length,
      missing: missing.slice(0, 3),
      totalMissing: missing.length,
      totalJdKeywords: jdKeywords.length,
    });
  };
  const scoreColor = (s) => s >= 75 ? (dark ? "#00d97e" : "#059669") : s >= 50 ? "#f59e0b" : (dark ? "#ff4d6d" : "#dc2626");
  const scoreLabel = (s) => s >= 75 ? "Strong Match" : s >= 50 ? "Needs Work" : "Critical Issues";

  const renderMarkdown = (text) => text.trim().split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**'))
      return <h3 key={i} style={{ fontSize: "16px", fontWeight: 700, color: t.text, marginTop: "20px", marginBottom: "6px" }}>{line.slice(2, -2)}</h3>;
    if (line.startsWith('*') && line.endsWith('*'))
      return <p key={i} style={{ fontSize: "12px", color: t.text4, marginBottom: "12px" }}>{line.slice(1, -1)}</p>;
    if (!line.trim()) return <div key={i} style={{ height: "6px" }} />;
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return <p key={i} style={{ fontSize: "14px", color: t.text2, lineHeight: 1.7, marginBottom: "4px" }}>
      {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
    </p>;
  });

  const inlineParse = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((p, j) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={j}>{p.slice(2, -2)}</strong>;
      const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) return <a key={j} href={m[2]} style={{ color: accent, fontWeight: 600, textDecoration: "underline" }}>{m[1]}</a>;
      return p;
    });
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Bricolage+Grotesque:wght@700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: ${t.border2}; border-radius: 3px; }
    textarea { resize: vertical; font-family: 'Plus Jakarta Sans', sans-serif; }
    .slide-up { animation: slideUp 0.3s ease-out forwards; }
    @keyframes slideUp { from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)} }
    .pulse { animation: pulse 1.4s ease-in-out infinite; }
    @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.45} }
    .plan-card { cursor:pointer; transition:all 0.2s; border-radius:16px; padding:28px 22px; position:relative; overflow:hidden; border:1.5px solid ${t.cardBorder}; background:${t.card}; box-shadow:${t.shadow}; }
    .plan-card:hover { border-color:${accent}; transform:translateY(-3px); box-shadow:0 12px 32px rgba(109,40,217,0.12); }
    .plan-card.featured { border-color:${accent}; box-shadow:0 0 0 1px ${accentBorder}, ${t.shadowLg}; }
    .btn-primary { background:${accent}; color:#fff; border:none; padding:13px 28px; border-radius:10px; font-size:15px; font-weight:600; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif; transition:all 0.18s; }
    .btn-primary:hover:not(:disabled) { background:#5b21b6; transform:translateY(-1px); box-shadow:0 6px 20px rgba(109,40,217,0.3); }
    .btn-primary:disabled { opacity:0.45; cursor:not-allowed; }
    .btn-ghost { background:transparent; color:${t.text3}; border:1.5px solid ${t.border}; padding:10px 20px; border-radius:10px; font-size:13px; font-weight:500; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif; transition:all 0.18s; }
    .btn-ghost:hover { border-color:${t.border2}; color:${t.text2}; }
    .card { background:${t.card}; border:1.5px solid ${t.cardBorder}; border-radius:14px; padding:24px; box-shadow:${t.shadow}; }
    .tag-match { background:${t.tagMatchBg}; color:${t.tagMatchColor}; border:1px solid ${t.tagMatchBorder}; padding:4px 10px; border-radius:20px; font-size:12px; font-weight:600; display:inline-block; margin:3px; }
    .tag-miss { background:${t.tagMissBg}; color:${t.tagMissColor}; border:1px solid ${t.tagMissBorder}; padding:4px 10px; border-radius:20px; font-size:12px; font-weight:600; display:inline-block; margin:3px; }
    .plan-card { cursor:pointer; transition:all 0.2s; border-radius:16px; padding:28px 22px; position:relative; overflow:hidden; border:1.5px solid ${t.cardBorder}; background:${t.card}; }
    .fix-row { display:flex; gap:10px; align-items:flex-start; padding:10px 0; border-bottom:1.5px solid ${t.fixRowBorder}; }
    .fix-row:last-child { border-bottom:none; }
    input[type="text"],input[type="password"] { background:${t.inputBg}; border:1.5px solid ${t.inputBorder}; border-radius:9px; padding:11px 14px; color:${t.text}; font-size:14px; font-family:'Plus Jakarta Sans',sans-serif; width:100%; outline:none; transition:border-color 0.18s; }
    input:focus { border-color:${accent}; }
    .toggle-track { width:44px; height:24px; border-radius:12px; background:${dark ? "#6d28d9" : "#e2e8f0"}; cursor:pointer; position:relative; transition:background 0.2s; border:none; padding:0; }
    .toggle-thumb { width:18px; height:18px; border-radius:50%; background:#fff; position:absolute; top:3px; left:${dark ? "23px" : "3px"}; transition:left 0.2s; box-shadow:0 1px 3px rgba(0,0,0,0.2); }
    .footer-link { color:${t.text3}; text-decoration:none; font-size:13px; cursor:pointer; transition:color 0.15s; background:none; border:none; font-family:'Plus Jakarta Sans',sans-serif; }
    .footer-link:hover { color:${accent}; }
    .how-step { display:flex; gap:16px; align-items:flex-start; padding:16px 0; border-bottom:1px solid ${t.border}; }
    .how-step:last-child { border-bottom:none; }
    .step-num { width:36px; height:36px; border-radius:10px; background:${accentLight}; border:1.5px solid ${accentBorder}; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:800; color:${accent}; flex-shrink:0; }
  `;

  const PolicyPage = ({ title, content }) => (
    <div className="slide-up" style={{ maxWidth: 680, margin: "0 auto", padding: "40px 20px" }}>
      <button onClick={() => goPage("main")} style={{ background: "none", border: "none", cursor: "pointer", color: t.text3, fontSize: "13px", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: "24px" }}>← Back to MyATScore</button>
      <div className="card" style={{ padding: "36px" }}>
        <h1 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "26px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>{title}</h1>
        <div style={{ width: 40, height: 3, background: accent, borderRadius: "2px", marginBottom: "24px" }} />
        {renderMarkdown(content)}
      </div>
    </div>
  );

  if (page === "privacy") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Privacy Policy" content={PRIVACY} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "terms") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Terms of Service" content={TERMS} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "refund") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Refund Policy" content={REFUND} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "about") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="About Us" content={ABOUT} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "contact") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Contact Us" content={CONTACT} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "shipping") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Delivery & Shipping Policy" content={SHIPPING} /><Footer t={t} goPage={goPage} /></div>;
  if (page === "faq") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><PolicyPage title="Frequently Asked Questions" content={FAQ} /><Footer t={t} goPage={goPage} /></div>;

  if (page === "admin") return <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}><style>{styles}</style><Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} /><BlogAdmin t={t} renderMarkdown={renderMarkdown} accent={accent} setPage={setPage} setSelectedBlogPost={setSelectedBlogPost} /><Footer t={t} goPage={goPage} /></div>;

  if (page === "blog") return (    <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}>
      <style>{styles}</style>
      <Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} />
      <div className="slide-up" style={{ maxWidth: 780, margin: "0 auto", padding: "40px 20px" }}>
        <button onClick={() => goPage("main")} style={{ background: "none", border: "none", cursor: "pointer", color: t.text3, fontSize: "13px", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: "24px" }}>← Back to MyATScore</button>
        <h1 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "32px", fontWeight: 800, color: t.text, marginBottom: "8px" }}>Resources & Career Insights</h1>
        <p style={{ color: t.text3, fontSize: "16px", marginBottom: "32px" }}>Expert advice on beating the ATS and landing your dream job.</p>

        <div style={{ display: "grid", gap: "20px" }}>
          {blogPosts.map(post => (
            <div key={post.id} className="card" style={{ cursor: "pointer", transition: "transform 0.2s" }} onClick={() => {
              setSelectedBlogPost(post);
              goPage("blog-post");
            }}>
              <div style={{ fontSize: "12px", color: accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>{post.date}</div>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "20px", fontWeight: 800, color: t.text, marginBottom: "12px" }}>{post.title}</h2>
              <p style={{ color: t.text2, fontSize: "14px", lineHeight: 1.6, marginBottom: "16px" }}>{post.excerpt}</p>
              <span style={{ color: accent, fontSize: "14px", fontWeight: 700 }}>Read Article →</span>
            </div>
          ))}
        </div>
      </div>
      <Footer t={t} goPage={goPage} />
    </div>
  );

  if (page === "blog-post" && selectedBlogPost) return (
    <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}>
      <style>{styles}</style>
      <Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} />
      <div className="slide-up" style={{ maxWidth: 680, margin: "0 auto", padding: "40px 20px" }}>
        <button onClick={() => goPage("blog")} style={{ background: "none", border: "none", cursor: "pointer", color: t.text3, fontSize: "13px", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: "24px" }}>← Back to Resources</button>
        <article className="card" style={{ padding: "40px" }}>
          <div style={{ fontSize: "12px", color: accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "12px" }}>{selectedBlogPost.date}</div>
          <h1 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "32px", fontWeight: 800, color: t.text, lineHeight: 1.2, marginBottom: "24px" }}>{selectedBlogPost.title}</h1>
          <div style={{ width: 60, height: 4, background: accent, borderRadius: "2px", marginBottom: "32px" }} />
          <div className="blog-content" style={{ color: t.text2, fontSize: "16px", lineHeight: 1.8 }}>
            {(selectedBlogPost.content || '').split('\n').map((line, i) => {
              if (line.startsWith('### ')) return <h3 key={i} style={{ fontSize: "20px", fontWeight: 800, color: t.text, marginTop: "32px", marginBottom: "12px" }}>{line.slice(4)}</h3>;
              if (line.startsWith('## ')) return <h2 key={i} style={{ fontSize: "24px", fontWeight: 800, color: t.text, marginTop: "40px", marginBottom: "16px" }}>{line.slice(3)}</h2>;
              if (line.startsWith('# ')) return <h2 key={i} style={{ fontSize: "26px", fontWeight: 800, color: t.text, marginTop: "8px", marginBottom: "20px", lineHeight: 1.3 }}>{line.slice(2)}</h2>;
              if (line.startsWith('- ') || line.startsWith('• ')) return <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "8px" }}><span style={{ color: accent, flexShrink: 0, fontWeight: 700 }}>•</span><span>{inlineParse(line.startsWith('• ') ? line.slice(2) : line.slice(2))}</span></div>;
              if (/^\d+\.\s/.test(line)) return <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "8px" }}><span style={{ color: accent, flexShrink: 0, fontWeight: 700 }}>{line.match(/^\d+/)[0]}.</span><span>{inlineParse(line.replace(/^\d+\.\s/, ''))}</span></div>;
              if (!line.trim()) return <div key={i} style={{ height: "12px" }} />;
              return <p key={i} style={{ marginBottom: "16px" }}>{inlineParse(line)}</p>;
            })}
          </div>
          <div style={{ marginTop: "40px", paddingTop: "32px", borderTop: `1px solid ${t.border}`, textAlign: "center" }}>
            <p style={{ fontSize: "14px", color: t.text3, marginBottom: "20px" }}>Want to see how your resume scores against these rules?</p>
            <button className="btn-primary" onClick={() => { setPage("main"); setStep("pricing"); scrollTop(); }}>Analyze My Resume Now →</button>
          </div>
        </article>
      </div>
      <Footer t={t} goPage={goPage} />
    </div>
  );
  return (
    <div ref={topRef} style={{ minHeight: "100vh", background: t.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", color: t.text }}>
      <style>{styles}</style>
      <Header t={t} dark={dark} setDark={setDark} credits={credits} goPage={goPage} setStep={setStep} scrollTop={scrollTop} changePlan={changePlan} step={step} page={page} setActivateTab={setActivateTab} onLogout={handleLogout} isLoggedIn={!!code} />
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "40px 20px" }}>

        {/* ── LANDING ── */}
        {step === "landing" && (
          <div className="slide-up">
            {/* Hero */}
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: accentLight, border: `1px solid ${accentBorder}`, padding: "6px 14px", borderRadius: "20px", fontSize: "12px", color: accent, marginBottom: "20px", fontWeight: 600 }}>
                <span>⚡</span> Pay per use. No subscriptions. Credits valid for 12 months of activity.
              </div>
              <h1 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "clamp(32px,6vw,52px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-2px", marginBottom: "20px", color: t.text }}>
                ATS Resume Checker &<br />
                <span style={{ background: `linear-gradient(135deg,${accent},#818cf8)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Score Optimizer</span>
              </h1>
              <p style={{ color: t.text3, fontSize: "17px", lineHeight: 1.7, maxWidth: 520, margin: "0 auto 32px" }}>
                Will your resume survive the ATS filter? MyATScore is an AI-powered resume checker for Workday, Greenhouse, Taleo, and Naukri. Get your compatibility score, missing keywords, and copy-paste fixes in 15 seconds.
              </p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", marginBottom: "20px" }}>
                <button className="btn-primary" onClick={() => { setStep("pricing"); scrollTop(); }} style={{ fontSize: "16px", padding: "14px 32px" }}>
                  Check My Resume →
                </button>
                <button className="btn-ghost" onClick={() => document.getElementById("how-it-works").scrollIntoView({ behavior: "smooth" })} style={{ fontSize: "15px" }}>
                  How it works
                </button>
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", color: t.text4, fontWeight: 500 }}>Or sign up with:</span>
                <div ref={googleBtnHeroRef}></div>
              </div>
            </div>

            {/* Problem Section */}
            <div className="card" style={{ marginBottom: "24px", background: dark ? "rgba(220,38,38,0.05)" : "rgba(254,242,242,1)", border: `1.5px solid ${dark ? "rgba(220,38,38,0.2)" : "rgba(254,202,202,1)"}` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                <div style={{ fontSize: "32px", flexShrink: 0 }}>⚠️</div>
                <div>
                  <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "18px", fontWeight: 800, color: dark ? "#ff4d6d" : "#dc2626", marginBottom: "8px" }}>What is ATS and why does it matter?</h2>
                  <p style={{ color: t.text2, fontSize: "14px", lineHeight: 1.7 }}>
                    ATS (Applicant Tracking System) is software used by 97% of Fortune 500 companies — including Google, Amazon, TCS, and Infosys — to automatically scan and rank resumes before a recruiter ever sees them. If your resume doesn't match the right keywords and format, it gets rejected instantly — no matter how qualified you are.
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "14px", marginBottom: "40px" }}>
              {[
                { stat: "75%", label: "Resumes filtered by ATS before a human sees them" },
                { stat: "97.8%", label: "Fortune 500 companies use ATS to screen applicants" },
                { stat: "3x", label: "More interview callbacks with an optimized resume" },
              ].map((s, i) => (
                <div key={i} className="card" style={{ textAlign: "center", padding: "20px 16px" }}>
                  <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "28px", fontWeight: 800, color: accent, marginBottom: "6px" }}>{s.stat}</div>
                  <div style={{ fontSize: "12px", color: t.text3, lineHeight: 1.5 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* How It Works */}
            <div id="how-it-works" style={{ marginBottom: "40px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>How it works</h2>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Get your ATS score in 3 simple steps</p>
              <div className="card">
                {[
                  { icon: "📋", title: "Paste your resume + job description", desc: "Copy your resume text and the job description you're applying for. Works for any role, any industry, any country." },
                  { icon: "🤖", title: "Our AI analyzes your ATS compatibility", desc: "We compare your resume against the job description using the same logic as ATS systems — keyword matching, format scoring, and skill gap analysis." },
                  { icon: "📊", title: "Get your score + actionable fixes", desc: "See your ATS score (0-100), matched and missing keywords, critical fixes, and an AI-rewritten professional summary — ready to copy-paste." },
                ].map((s, i) => (
                  <div key={i} className="how-step">
                    <div className="step-num">{i + 1}</div>
                    <div>
                      <div style={{ fontSize: "15px", fontWeight: 700, color: t.text, marginBottom: "4px" }}>{s.icon} {s.title}</div>
                      <div style={{ fontSize: "13px", color: t.text3, lineHeight: 1.6 }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What You Get */}
            <div style={{ marginBottom: "40px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>What you get</h2>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Every analysis includes all of these</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {[
                  { icon: "🎯", title: "ATS Score (0-100)", desc: "A 5-dimension calibrated score: keyword match, experience fit, section completeness, ATS formatting, and language quality" },
                  { icon: "✅", title: "Matched Keywords", desc: "Keywords your resume already has that ATS filters will pick up and score" },
                  { icon: "❌", title: "Missing Keywords", desc: "Critical keywords you need to add to pass ATS filters for this role" },
                  { icon: "🔧", title: "Critical Fixes", desc: "Specific, ranked changes ordered by how much each will improve your score" },
                  { icon: "✏️", title: "Bullet Rewrites", desc: "2–3 of your weakest bullets rewritten with keywords and metrics — copy-paste ready" },
                  { icon: "✨", title: "AI Summary Rewrite", desc: "Professional summary rewritten by AI and tuned to the specific job description" },
                ].map((item, i) => (
                  <div key={i} className="card" style={{ padding: "16px" }}>
                    <div style={{ fontSize: "20px", marginBottom: "6px" }}>{item.icon}</div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: t.text, marginBottom: "3px" }}>{item.title}</div>
                    <div style={{ fontSize: "12px", color: t.text3, lineHeight: 1.5 }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div style={{ marginBottom: "40px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>What job seekers say</h2>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Real results from people who fixed their resumes</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "14px" }}>
                {[
                  { name: "Priya S.", role: "Software Engineer, Bangalore", quote: "Got my score from 44 to 89 after applying the fixes. Had 3 interview calls within the same week. The bullet rewrites were exactly what I needed.", stars: 5 },
                  { name: "Rahul V.", role: "MBA Graduate, Hyderabad", quote: "The section check showed my summary was missing — I had no idea that mattered. Added it using the suggested rewrite and my recruiter callback rate doubled within a week.", stars: 5 },
                  { name: "Ananya K.", role: "Data Analyst, Chennai", quote: "Finally a tool that tells you what's actually wrong instead of vague advice. The missing keywords were spot on — exactly what the job description asked for.", stars: 5 },
                  { name: "Marcus T.", role: "Product Manager, London", quote: "Applying for UK roles and this caught formatting issues my Workday applications were failing on. Jumped from no responses to 4 interviews in 2 weeks.", stars: 5 },
                ].map((testimonial, i) => (
                  <div key={i} className="card" style={{ padding: "20px" }}>
                    <div style={{ fontSize: "14px", color: "#f59e0b", marginBottom: "10px" }}>{"★★★★★"}</div>
                    <p style={{ fontSize: "13px", color: t.text2, lineHeight: 1.7, marginBottom: "12px", fontStyle: "italic" }}>"{testimonial.quote}"</p>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: t.text }}>{testimonial.name}</div>
                    <div style={{ fontSize: "11px", color: t.text4 }}>{testimonial.role}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Free Keyword Counter */}
            <div style={{ marginBottom: "40px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text }}>Try it free</h2>
                <div style={{ background: dark ? "rgba(0,217,126,0.12)" : "rgba(5,150,105,0.08)", border: `1px solid ${dark ? "rgba(0,217,126,0.25)" : "rgba(5,150,105,0.2)"}`, color: dark ? "#00d97e" : "#059669", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700 }}>FREE</div>
              </div>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Paste your resume and job description. Get an instant keyword match preview — no payment needed.</p>

              <div style={{ display: "grid", gap: "12px", marginBottom: "14px" }}>
                {[
                  { label: "Your Resume", val: freeResume, set: setFreeResume, ph: "Paste your resume text here..." },
                  { label: "Job Description", val: freeJd, set: setFreeJd, ph: "Paste the job description here..." },
                ].map((f, i) => (
                  <div key={i} className="card" style={{ padding: "16px" }}>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "0.8px", display: "block", marginBottom: "8px" }}>{f.label}</label>
                    <textarea value={f.val} onChange={(e) => f.set(e.target.value)} placeholder={f.ph}
                      style={{ width: "100%", minHeight: "100px", background: t.inputBg, border: `1.5px solid ${t.inputBorder}`, borderRadius: "9px", padding: "10px", color: t.text, fontSize: "13px", lineHeight: 1.6, outline: "none", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                      onFocus={(e) => e.target.style.borderColor = accent} onBlur={(e) => e.target.style.borderColor = t.inputBorder} />
                  </div>
                ))}
              </div>

              <button onClick={runFreeCheck} disabled={!freeResume.trim() || !freeJd.trim()}
                style={{ width: "100%", padding: "13px", background: dark ? "rgba(109,40,217,0.15)" : "rgba(109,40,217,0.08)", border: `1.5px solid ${accentBorder}`, borderRadius: "10px", color: accent, fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: (!freeResume.trim() || !freeJd.trim()) ? 0.5 : 1 }}>
                Check Keyword Match (Free) →
              </button>

              {freeResult && (
                <div className="slide-up card" style={{ marginTop: "16px", border: `1.5px solid ${accentBorder}`, background: accentLight }}>
                  {/* Score */}
                  <div style={{ textAlign: "center", padding: "20px 0", borderBottom: `1px solid ${t.border}`, marginBottom: "16px" }}>
                    <div style={{ fontSize: "11px", color: t.text4, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px", fontWeight: 600 }}>Keyword Match Preview</div>
                    <div style={{ fontSize: "56px", fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 800, color: scoreColor(freeResult.score), lineHeight: 1 }}>
                      {freeResult.score}<span style={{ fontSize: "0.3em", color: t.text4 }}>/100</span>
                    </div>
                    <div style={{ display: "inline-block", background: `${scoreColor(freeResult.score)}15`, border: `1px solid ${scoreColor(freeResult.score)}30`, color: scoreColor(freeResult.score), padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, marginTop: "8px" }}>
                      {freeResult.score >= 60 ? "Good Match" : freeResult.score >= 40 ? "Partial Match" : "Low Match"}
                    </div>
                    <div style={{ fontSize: "11px", color: t.text4, marginTop: "8px" }}>Keyword-only · Full score includes experience, formatting & more</div>
                  </div>

                  {/* Matched keywords */}
                  <div style={{ marginBottom: "14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#00d97e" : "#059669", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "8px" }}>
                      ✓ Matched ({freeResult.totalMatched} of {freeResult.totalJdKeywords} keywords)
                    </div>
                    <div>{freeResult.matched.map((k, i) => <span key={i} className="tag-match">{k}</span>)}</div>
                  </div>

                  {/* Missing keywords teaser */}
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#ff4d6d" : "#dc2626", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "8px" }}>
                      ✗ Missing Keywords ({freeResult.totalMissing} found)
                    </div>
                    <div>{freeResult.missing.map((k, i) => <span key={i} className="tag-miss">{k}</span>)}</div>
                    {freeResult.totalMissing > 3 && (
                      <div style={{ fontSize: "12px", color: t.text3, marginTop: "8px", fontStyle: "italic" }}>
                        + {freeResult.totalMissing - 3} more missing keywords hidden — get full analysis below
                      </div>
                    )}
                  </div>

                  {/* Upgrade CTA */}
                  <div style={{ background: t.card, borderRadius: "10px", padding: "16px", textAlign: "center", border: `1px solid ${t.border}` }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: t.text, marginBottom: "4px" }}>Want the full analysis?</div>
                    <div style={{ fontSize: "12px", color: t.text3, marginBottom: "12px" }}>Get all missing keywords, critical fixes, strengths, and an AI-rewritten summary</div>
                    <button className="btn-primary" onClick={() => { setStep("pricing"); scrollTop(); }} style={{ padding: "11px 24px", fontSize: "14px" }}>
                      {currencyLoading ? "Get Full Analysis →" : `Get Full Analysis → from ${fmtPrice(299)}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* FAQ */}
            <div style={{ marginBottom: "40px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>Common questions</h2>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Everything you need to know before you start</p>
              <div className="card" style={{ padding: 0 }}>
                {[
                  { q: "Is this just ChatGPT or Gemini with a different name?", a: "No. MyATScore scores your resume across 5 measurable dimensions — keyword match (30pts), experience relevance (25pts), section completeness (20pts), ATS formatting (15pts), and language impact (10pts). You see the exact sub-score breakdown. It also rewrites 2-3 weak bullets from your actual resume and detects missing sections — things a generic chatbot won't do structurally." },
                  { q: "How is the ATS score calculated?", a: "Your score is the sum of 5 sub-scores totalling 100. The full breakdown is shown in your report, so you know exactly which dimension is hurting you most and where to focus your edits first." },
                  { q: "Is my resume data safe?", a: "Yes. Your resume text is used only to generate your analysis and is not stored after your session ends. We never use your data to train AI models. No resume content is shared with any third party beyond what's needed to produce your score." },
                  { q: "Why pay when free tools exist?", a: "Free tools give you a basic keyword percentage — no direction. MyATScore gives you a 5-dimension breakdown, section-by-section detection, 2-3 actual bullet rewrites you can copy straight into your resume, and an AI-rewritten summary tuned to the specific job. That depth directly translates into more interview calls." },
                  { q: "What if the analysis fails?", a: "If a credit is consumed and the analysis fails due to a technical error on our side, email support@myatscore.com and we restore your credit. We respond within 2 business days." },
                ].map((item, i, arr) => (
                  <div key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${t.border}` : "none" }}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: t.text, lineHeight: 1.5 }}>{item.q}</span>
                      <span style={{ fontSize: "20px", color: t.text4, marginLeft: "16px", flexShrink: 0, lineHeight: 1 }}>{openFaq === i ? "−" : "+"}</span>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding: "0 20px 16px", fontSize: "13px", color: t.text3, lineHeight: 1.8 }}>{item.a}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA to pricing */}
            <div style={{ textAlign: "center", background: accentLight, border: `1.5px solid ${accentBorder}`, borderRadius: "16px", padding: "32px 24px", marginBottom: "20px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "8px" }}>Ready to fix your resume?</h2>
              <p style={{ color: t.text3, fontSize: "14px", marginBottom: "20px" }}>Pay once per analysis. No subscription. Credits valid for 12 months of activity.</p>
              <button className="btn-primary" onClick={() => { setStep("pricing"); scrollTop(); }} style={{ fontSize: "16px", padding: "14px 36px" }}>
                See Plans & Pricing →
              </button>
            </div>
          </div>
        )}

        {/* ── PRICING ── */}
        {step === "pricing" && (
          <div className="slide-up">
            {paymentError && (
              <div style={{ background: "rgba(220,38,38,0.08)", border: "1.5px solid rgba(220,38,38,0.2)", borderRadius: "12px", padding: "14px 18px", marginBottom: "20px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <span style={{ fontSize: "18px" }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 700, color: "#dc2626", fontSize: "14px", marginBottom: "4px" }}>
                    {paymentError.includes("cancelled") ? "Payment Cancelled" : "Payment Failed"}
                  </div>
                  <div style={{ fontSize: "13px", color: "#374151" }}>{paymentError}</div>
                  <button onClick={() => setPaymentError("")} style={{ background: "none", border: "none", color: "#dc2626", fontSize: "12px", cursor: "pointer", marginTop: "6px", padding: 0, fontFamily: "'Plus Jakarta Sans',sans-serif", textDecoration: "underline" }}>Dismiss</button>
                </div>
              </div>
            )}
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <h1 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "clamp(28px,5vw,40px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-1px", marginBottom: "12px", color: t.text }}>
                Simple, transparent pricing
              </h1>
              <p style={{ color: t.text3, fontSize: "15px", lineHeight: 1.7, maxWidth: 440, margin: "0 auto" }}>
                Buy credits once, use them at your own pace. No monthly fees. No surprises.
              </p>

              {/* Currency Selector */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "20px" }}>
                <span style={{ fontSize: "12px", color: t.text3, fontWeight: 600 }}>🌍 Show prices in:</span>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={{ background: t.inputBg, border: `1.5px solid ${t.inputBorder}`, borderRadius: "8px", padding: "6px 12px", fontSize: "13px", fontWeight: 600, color: t.text, fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: "pointer", outline: "none" }}
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                  ))}
                </select>
                {currencyLoading && <span style={{ fontSize: "11px", color: t.text4 }}>detecting...</span>}
                {!currencyLoading && currency !== "INR" && <span style={{ fontSize: "11px", color: t.text4 }}>≈ indicative · billed in ₹INR</span>}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "16px", marginBottom: "28px" }}>
              {PLANS.map((plan) => (
                <div key={plan.id} className={`plan-card ${plan.id === "starter" ? "featured" : ""}`} onClick={() => selectPlan(plan)}>
                  {plan.tag && <div style={{ position: "absolute", top: 0, right: 0, background: plan.id === "starter" ? `linear-gradient(135deg,${accent},#4f46e5)` : "linear-gradient(135deg,#0891b2,#0e7490)", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "5px 12px", borderRadius: "0 14px 0 10px", letterSpacing: "0.5px", textTransform: "uppercase" }}>{plan.tag}</div>}
                  <div style={{ fontSize: "11px", color: t.text3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "10px" }}>{plan.name}</div>
                  <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "38px", fontWeight: 800, color: t.text, marginBottom: "4px", letterSpacing: "-1px" }}>{fmtPrice(plan.inr)}</div>
                  <div style={{ fontSize: "13px", color: t.text3, marginBottom: "20px" }}>{plan.desc}</div>
                  <div style={{ borderTop: `1.5px solid ${t.border}`, paddingTop: "16px", marginBottom: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                      <div style={{ width: 30, height: 30, background: `${plan.color}18`, border: `1.5px solid ${plan.color}35`, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: 800, color: plan.color }}>{plan.credits}</div>
                      <span style={{ fontSize: "14px", color: t.text2, fontWeight: 500 }}>resume {plan.credits === 1 ? "analysis" : "analyses"}</span>
                    </div>
                    <div style={{ fontSize: "12px", color: t.text4, fontWeight: 500 }}>{fmtPerAnalysis(plan.perAnalysisInr)}</div>
                  </div>
                  <div style={{ background: plan.id === "starter" ? accent : accentLight, border: `1.5px solid ${plan.id === "starter" ? accent : accentBorder}`, borderRadius: "9px", padding: "10px 14px", textAlign: "center", fontSize: "14px", fontWeight: 600, color: plan.id === "starter" ? "#fff" : accent }}>
                    Get Started →
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "13px", color: t.text4 }}>✓ Secure payment &nbsp;•&nbsp; ✓ Credits valid for 12 months of activity &nbsp;•&nbsp; ✓ No subscription</p>
              <button onClick={() => setStep("landing")} className="btn-ghost" style={{ marginTop: "12px", fontSize: "12px" }}>← Back</button>
            </div>
          </div>
        )}

        {/* ── ACTIVATE ── */}
        {step === "activate" && (
          <div className="slide-up">
            {paymentSuccess && (
              <div style={{ background: "rgba(5,150,105,0.08)", border: "1.5px solid rgba(5,150,105,0.25)", borderRadius: "12px", padding: "20px 24px", marginBottom: "20px", textAlign: "center" }}>
                <div style={{ fontSize: "36px", marginBottom: "8px" }}>🎉</div>
                <div style={{ fontWeight: 700, color: "#059669", fontSize: "17px", marginBottom: "6px" }}>Payment Successful!</div>
                <div style={{ fontSize: "13px", color: t.text2, lineHeight: 1.7 }}>
                  Your activation code has been sent to <strong>{email || "your email"}</strong>.<br/>
                  Check your inbox and spam folder for an email from <strong>noreply@myatscore.com</strong>.<br/>
                  Enter your code and access key below to unlock your credits.
                </div>
              </div>
            )}

            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "28px", fontWeight: 800, color: t.text, marginBottom: "8px", letterSpacing: "-0.5px" }}>
                {selectedPlan ? "Unlock Your Credits" : "Access Your Account"}
              </h2>
              <p style={{ color: t.text3, fontSize: "15px", maxWidth: "400px", margin: "0 auto" }}>
                {selectedPlan ? "Choose how you want to activate your credits and start your analysis." : "Log in to view your remaining credits and resume your session."}
              </p>
            </div>

            {/* ── TAB SWITCHER ── */}
            {selectedPlan && (
              <div style={{ background: t.bg3, padding: "4px", borderRadius: "12px", display: "flex", gap: "4px", marginBottom: "24px", maxWidth: "400px", margin: "0 auto 32px" }}>
                <button
                  onClick={() => setActivateTab("pay")}
                  style={{
                    flex: 1, padding: "10px", borderRadius: "9px", border: "none", fontSize: "13px", fontWeight: 700, cursor: "pointer",
                    background: activateTab === "pay" ? t.card : "transparent",
                    color: activateTab === "pay" ? accent : t.text3,
                    boxShadow: activateTab === "pay" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s"
                  }}
                >
                  1. Pay Now
                </button>
                <button
                  onClick={() => setActivateTab("login")}
                  style={{
                    flex: 1, padding: "10px", borderRadius: "9px", border: "none", fontSize: "13px", fontWeight: 700, cursor: "pointer",
                    background: activateTab === "login" ? t.card : "transparent",
                    color: activateTab === "login" ? accent : t.text3,
                    boxShadow: activateTab === "login" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s"
                  }}
                >
                  2. Use Code / Login
                </button>
              </div>
            )}

            {/* ── TAB: PAY ── */}
            {activateTab === "pay" && selectedPlan && (
              <div className="card" style={{ marginBottom: "24px", border: `1.5px solid ${accentBorder}` }}>
                <div style={{ background: accentLight, margin: "-24px -24px 24px -24px", padding: "24px", borderRadius: "14px 14px 0 0", borderBottom: `1.5px solid ${accentBorder}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Selected Plan</div>
                      <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "22px", fontWeight: 800, color: t.text }}>{selectedPlan.name} Plan</div>
                      <div style={{ fontSize: "13px", color: t.text3, marginTop: "2px" }}>{selectedPlan.credits} resume analysis credits</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "28px", fontWeight: 800, color: t.text }}>{fmtPrice(selectedPlan.inr)}</div>
                      <div style={{ fontSize: "11px", color: t.text4, fontWeight: 600 }}>indicative price</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: "8px" }}>Activation Email</label>
                  <input type="text" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                  <div style={{ fontSize: "11px", color: t.text4, marginTop: "6px" }}>Where we'll send your activation code and receipt.</div>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: "12px" }}>Payment Method</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    {[
                      { id: "razorpay", name: "Razorpay", desc: "Cards, UPI, Netbanking" },
                      { id: "ccavenue", name: "CCAvenue", desc: "Wallets & Netbanking" }
                    ].map(m => (
                      <button
                        key={m.id}
                        onClick={() => setPaymentMethod(m.id)}
                        style={{
                          padding: "14px", borderRadius: "12px", textAlign: "left", cursor: "pointer",
                          border: `2px solid ${paymentMethod === m.id ? accent : t.border}`,
                          background: paymentMethod === m.id ? `${accent}05` : t.card,
                          transition: "all 0.2s"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                          <span style={{ fontSize: "14px", fontWeight: 700, color: paymentMethod === m.id ? accent : t.text }}>{m.name}</span>
                          {paymentMethod === m.id && <span style={{ color: accent }}>✓</span>}
                        </div>
                        <div style={{ fontSize: "11px", color: t.text3, lineHeight: 1.3 }}>{m.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {paymentMethod === "razorpay" && (
                  <div style={{ background: t.bg3, padding: "16px", borderRadius: "12px", marginBottom: "24px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Mobile Number (optional)</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <select
                        value={razorpayIsd}
                        onChange={(e) => setRazorpayIsd(e.target.value)}
                        style={{ background: t.inputBg, border: `1.5px solid ${t.inputBorder}`, borderRadius: "9px", padding: "10px", fontSize: "13px", fontWeight: 600, color: t.text, cursor: "pointer", outline: "none" }}
                      >
                        {ISD_CODES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                      </select>
                      <input
                        type="tel"
                        placeholder="Mobile number"
                        value={razorpayPhone}
                        onChange={(e) => setRazorpayPhone(e.target.value.replace(/[^0-9]/g, ""))}
                        maxLength={12}
                        style={{ flex: 1, marginBottom: 0 }}
                      />
                    </div>
                  </div>
                )}

                {paymentMethod === "ccavenue" && (
                  <div style={{ background: t.bg3, padding: "16px", borderRadius: "12px", marginBottom: "24px", display: "grid", gap: "10px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px" }}>🏦 Billing Details</div>
                    <input type="text" placeholder="Full Name *" value={billingName} onChange={(e) => setBillingName(e.target.value)} style={{ marginBottom: 0 }} />
                    <input type="tel" placeholder="Phone Number *" value={billingPhone} onChange={(e) => setBillingPhone(e.target.value)} style={{ marginBottom: 0 }} />
                    <input ref={addressInputRef} type="text" placeholder="🔍 Start typing your address..." value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} autoComplete="off" style={{ marginBottom: 0 }} />
                  </div>
                )}

                <div style={{ background: t.bg3, padding: "16px", borderRadius: "12px", marginBottom: "24px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Discord User ID <span style={{ color: t.text4, fontWeight: 400, textTransform: "none" }}>(optional — auto-unlocks /atscheck bot feature)</span></div>
                  <input
                    type="text"
                    placeholder="e.g. 123456789012345678"
                    value={discordId}
                    onChange={(e) => setDiscordId(e.target.value.replace(/[^0-9]/g, "").substring(0, 20))}
                    style={{ marginBottom: 0 }}
                  />
                  <div style={{ fontSize: "11px", color: t.text4, marginTop: "6px" }}>Find yours: Discord → Settings → Advanced → Developer Mode on → right-click your name → Copy User ID</div>
                </div>

                <button className="btn-primary" onClick={handlePayment} disabled={paying} style={{ width: "100%", padding: "16px", fontSize: "16px" }}>
                  {paying ? "Preparing payment..." : `Pay ${fmtPrice(selectedPlan.inr)} Now →`}
                </button>
                <div style={{ fontSize: "12px", color: t.text4, textAlign: "center", marginTop: "14px" }}>🔒 Secure encrypted payment · Powered by {paymentMethod === "razorpay" ? "Razorpay" : "CCAvenue"}</div>
              </div>
            )}

            {/* ── TAB: LOGIN ── */}
            {(activateTab === "login" || !selectedPlan) && (
              <div className="card" style={{ marginBottom: "24px" }}>
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "16px" }}>Quick Access</div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div ref={googleBtnLoginRef}></div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                  <div style={{ flex: 1, height: "1px", background: t.border }}></div>
                  <span style={{ fontSize: "11px", color: t.text4, fontWeight: 700 }}>OR USE YOUR CODE</span>
                  <div style={{ flex: 1, height: "1px", background: t.border }}></div>
                </div>

                <div style={{ display: "grid", gap: "12px", marginBottom: "20px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: "8px" }}>Activation Code</label>
                    <input type="text" placeholder="ATS-XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ letterSpacing: "2px", textAlign: "center", fontWeight: 700 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: "8px" }}>Access Key</label>
                    <div style={{ position: "relative" }}>
                      <input type={showKey ? "text" : "password"} placeholder="Enter your private access key" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} style={{ paddingRight: "50px" }} />
                      <button onClick={() => setShowKey(!showKey)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: accent, cursor: "pointer", fontSize: "11px", fontWeight: 700 }}>{showKey ? "HIDE" : "SHOW"}</button>
                    </div>
                  </div>
                </div>

                {activationError && <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: "10px", padding: "12px 15px", fontSize: "13px", color: "#dc2626", marginBottom: "16px", textAlign: "center" }}>⚠ {activationError}</div>}

                <button className="btn-primary" onClick={activate} style={{ width: "100%", padding: "16px", fontSize: "16px" }}>
                  Unlock Credits →
                </button>

                <div style={{ textAlign: "center", marginTop: "24px", paddingTop: "20px", borderTop: `1px solid ${t.border}` }}>
                  <button onClick={() => setShowRestore(!showRestore)} style={{ background: "none", border: "none", color: accent, cursor: "pointer", fontSize: "13px", fontWeight: 600, textDecoration: "underline" }}>
                    Lost your code? Restore my credits →
                  </button>
                </div>

                {showRestore && (
                  <div style={{ marginTop: "20px", padding: "20px", background: t.bg3, borderRadius: "12px", border: `1.5px solid ${accentBorder}` }}>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>Restore Access</div>
                    <p style={{ fontSize: "12px", color: t.text3, marginBottom: "16px", lineHeight: 1.6 }}>Enter your registered email and we'll resend your credentials.</p>
                    <input type="text" placeholder="your@email.com" value={restoreEmail} onChange={(e) => setRestoreEmail(e.target.value)} style={{ marginBottom: "12px" }} />
                    {restoreStatus === "sent" && <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "#059669", marginBottom: "12px" }}>✓ Check your inbox for your code.</div>}
                    <button className="btn-primary" onClick={handleResendCode} disabled={resending} style={{ width: "100%", padding: "12px", fontSize: "14px" }}>
                      {resending ? "Sending..." : "Resend Credentials →"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ textAlign: "center" }}>
              <button className="btn-ghost" onClick={changePlan} style={{ fontSize: "13px", fontWeight: 600 }}>← Back to Plans</button>
            </div>
          </div>
        )}

        {/* ── INPUT ── */}
        {step === "input" && (
          <div className="slide-up">
            {selectedPlan && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: accentLight, border: `1.5px solid ${accentBorder}`, borderRadius: "12px", padding: "12px 18px", marginBottom: "24px" }}>
                <div style={{ fontSize: "13px", color: accent, fontWeight: 600 }}>
                  {selectedPlan.name} Plan &nbsp;·&nbsp; <span style={{ color: t.text3, fontWeight: 400 }}>{credits > 0 ? `${credits} of ${selectedPlan.credits} credits remaining` : "Credits exhausted — please top up to continue"}</span>
                </div>
                <div style={{ display: "flex", gap: "5px" }}>
                  {Array.from({ length: Math.min(selectedPlan.credits, 10) }).map((_, i) => (
                    <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i < credits ? accent : t.border2 }} />
                  ))}
                  {selectedPlan.credits > 10 && <span style={{ fontSize: "11px", color: t.text4 }}>+{selectedPlan.credits - 10}</span>}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "22px" }}>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "24px", fontWeight: 800, color: t.text, marginBottom: "6px" }}>Analyze Your Resume</h2>
              <p style={{ color: t.text3, fontSize: "14px" }}>Paste your resume and the job description below. Works for any role, any industry, any country.</p>
            </div>

            <div style={{ display: "grid", gap: "14px", marginBottom: "14px" }}>
              {[
                { label: "Your Resume", icon: "📄", val: resume, set: setResume, ph: "Paste your full resume text here (copy from Word or PDF)...", min: 50 },
                { label: "Job Description", icon: "📋", val: jd, set: setJd, ph: "Paste the full job description here...", min: 20 },
              ].map((f, i) => (
                <div key={i} className="card">
                  <label style={{ fontSize: "12px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "0.8px", display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                    <span>{f.icon}</span>{f.label}
                  </label>
                  <textarea value={f.val} onChange={(e) => f.set(e.target.value)} 
                    placeholder={credits > 0 ? f.ph : "Please get more credits to start your analysis..."}
                    readOnly={credits <= 0}
                    style={{ width: "100%", minHeight: "160px", background: credits > 0 ? t.inputBg : t.bg3, border: `1.5px solid ${t.inputBorder}`, borderRadius: "9px", padding: "12px", color: t.text, fontSize: "13px", lineHeight: 1.6, outline: "none", transition: "border-color 0.18s", cursor: credits > 0 ? "text" : "not-allowed" }}
                    onFocus={(e) => credits > 0 && (e.target.style.borderColor = accent)} onBlur={(e) => e.target.style.borderColor = t.inputBorder} />
                  <div style={{ fontSize: "11px", color: t.text4, marginTop: "6px" }}>
                    {f.val ? `${f.val.split(/\s+/).filter(Boolean).length} words` : `Minimum ${f.min} words required`}
                  </div>
                </div>
              ))}
            </div>

            {error && <div style={{ background: dark ? "rgba(255,77,109,0.08)" : "rgba(220,38,38,0.06)", border: `1px solid ${dark ? "rgba(255,77,109,0.2)" : "rgba(220,38,38,0.15)"}`, borderRadius: "10px", padding: "11px 15px", fontSize: "13px", color: dark ? "#ff4d6d" : "#dc2626", marginBottom: "14px" }}>⚠ {error}</div>}

            {credits > 0 ? (
              <button className="btn-primary" onClick={analyze} disabled={loading} style={{ width: "100%", fontSize: "15px", padding: "15px" }}>
                {loading ? <span className="pulse">Analyzing your resume...</span> : `Check My ATS Score → (${credits} credit${credits !== 1 ? "s" : ""} remaining)`}
              </button>
            ) : (
              <button className="btn-primary" onClick={changePlan} style={{ width: "100%", fontSize: "15px", padding: "15px" }}>
                Get More Credits to Analyze →
              </button>
            )}
            <div style={{ textAlign: "center", marginTop: "12px", fontSize: "12px", color: t.text4 }}>Results in ~15 seconds · Secure · No data stored</div>
          </div>
        )}

        {/* ── RESULT ── */}
        {step === "result" && result && (
          <div ref={resultRef} className="slide-up">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: credits > 0 ? (dark ? "rgba(0,217,126,0.07)" : "rgba(5,150,105,0.06)") : (dark ? "rgba(255,77,109,0.07)" : "rgba(220,38,38,0.06)"), border: `1.5px solid ${credits > 0 ? (dark ? "rgba(0,217,126,0.2)" : "rgba(5,150,105,0.2)") : (dark ? "rgba(255,77,109,0.2)" : "rgba(220,38,38,0.15)")}`, borderRadius: "12px", padding: "10px 16px", marginBottom: "20px" }}>
              {credits > 0
                ? <span style={{ color: dark ? "#00d97e" : "#059669", fontWeight: 600, fontSize: "13px" }}>✓ {credits} credit{credits !== 1 ? "s" : ""} remaining</span>
                : <span style={{ color: dark ? "#ff4d6d" : "#dc2626", fontWeight: 600, fontSize: "13px" }}>Credits exhausted</span>}
              {credits === 0 && <button className="btn-primary" onClick={changePlan} style={{ padding: "7px 16px", fontSize: "12px" }}>Get More Credits</button>}
            </div>

            <div className="card" style={{ textAlign: "center", marginBottom: "16px", padding: "40px 24px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${scoreColor(result.ats_score)}10 0%, transparent 60%)`, pointerEvents: "none" }} />
              <div style={{ fontSize: "12px", color: t.text4, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "14px", fontWeight: 700 }}>ATS Compatibility Score</div>
              <div style={{ fontSize: "clamp(64px,12vw,92px)", fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 800, color: scoreColor(result.ats_score), lineHeight: 1, marginBottom: "10px", letterSpacing: "-2px" }}>
                {result.ats_score}<span style={{ fontSize: "0.3em", color: t.text4, letterSpacing: 0 }}>/100</span>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: `${scoreColor(result.ats_score)}15`, border: `1.5px solid ${scoreColor(result.ats_score)}30`, color: scoreColor(result.ats_score), padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, marginBottom: "14px" }}>
                {scoreLabel(result.ats_score)}
              </div>
              <p style={{ color: t.text3, fontSize: "14px", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>{result.verdict}</p>
            </div>

            {/* Score Breakdown */}
            {result.score_breakdown && (
              <div className="card" style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "14px" }}>📊 Score Breakdown</div>
                {[
                  { label: "Keyword Match", key: "keyword_match", max: 30 },
                  { label: "Experience Relevance", key: "experience_relevance", max: 25 },
                  { label: "Section Completeness", key: "section_completeness", max: 20 },
                  { label: "ATS Formatting", key: "formatting_ats_friendliness", max: 15 },
                  { label: "Language & Impact", key: "language_impact", max: 10 },
                ].map(({ label, key, max }) => {
                  const val = result.score_breakdown[key] ?? 0;
                  const pct = Math.round((val / max) * 100);
                  const bar = pct >= 70 ? scoreColor(80) : pct >= 45 ? "#f59e0b" : scoreColor(0);
                  return (
                    <div key={key} style={{ marginBottom: "10px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: t.text2, marginBottom: "5px" }}>
                        <span>{label}</span>
                        <span style={{ fontWeight: 700, color: bar }}>{val}<span style={{ color: t.text4, fontWeight: 400 }}>/{max}</span></span>
                      </div>
                      <div style={{ height: "5px", background: t.border, borderRadius: "3px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: bar, borderRadius: "3px" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
              <div className="card">
                <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#00d97e" : "#059669", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>✓ Matched ({result.matched_keywords?.length})</div>
                {result.matched_keywords?.map((k, i) => <span key={i} className="tag-match">{k}</span>)}
              </div>
              <div className="card">
                <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#ff4d6d" : "#dc2626", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>✗ Missing ({result.missing_keywords?.length})</div>
                {result.missing_keywords?.map((k, i) => <span key={i} className="tag-miss">{k}</span>)}
              </div>
            </div>

            {/* Section Check */}
            {result.section_check && (
              <div className="card" style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: t.text3, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "12px" }}>📋 Resume Section Check</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  {[
                    { key: "contact_info", label: "Contact Info" },
                    { key: "professional_summary", label: "Professional Summary" },
                    { key: "work_experience", label: "Work Experience" },
                    { key: "education", label: "Education" },
                    { key: "skills_section", label: "Skills Section" },
                    { key: "certifications", label: "Certifications" },
                  ].map(({ key, label }) => {
                    const ok = result.section_check[key];
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: ok ? (dark ? "rgba(0,217,126,0.07)" : "rgba(5,150,105,0.05)") : (dark ? "rgba(255,77,109,0.07)" : "rgba(220,38,38,0.05)"), borderRadius: "8px", border: `1px solid ${ok ? (dark ? "rgba(0,217,126,0.2)" : "rgba(5,150,105,0.15)") : (dark ? "rgba(255,77,109,0.2)" : "rgba(220,38,38,0.12)")}` }}>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: ok ? (dark ? "#00d97e" : "#059669") : (dark ? "#ff4d6d" : "#dc2626") }}>{ok ? "✓" : "✗"}</span>
                        <span style={{ fontSize: "11px", color: t.text2, fontWeight: 500 }}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="card" style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "12px" }}>🔧 Critical Fixes</div>
              {result.critical_fixes?.map((fix, i) => (
                <div key={i} className="fix-row">
                  <div style={{ minWidth: 22, height: 22, background: "rgba(245,158,11,0.12)", border: "1.5px solid rgba(245,158,11,0.25)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 800, color: "#f59e0b" }}>{i + 1}</div>
                  <span style={{ fontSize: "13px", color: t.text2, lineHeight: 1.6 }}>{fix}</span>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
              <div className="card">
                <div style={{ fontSize: "11px", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>💪 Strengths</div>
                {result.strengths?.map((s, i) => <div key={i} style={{ fontSize: "13px", color: t.text2, padding: "7px 0", borderBottom: `1px solid ${t.fixRowBorder}`, lineHeight: 1.5 }}>{s}</div>)}
              </div>
              <div className="card">
                <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#60a5fa" : "#2563eb", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>🎯 Quick Wins</div>
                {result.quick_wins?.map((qw, i) => <div key={i} style={{ fontSize: "13px", color: t.text2, padding: "7px 0", borderBottom: `1px solid ${t.fixRowBorder}`, lineHeight: 1.5 }}>{qw}</div>)}
              </div>
            </div>

            {/* Bullet Rewrites */}
            {result.bullet_rewrites && result.bullet_rewrites.length > 0 && (
              <div className="card" style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: dark ? "#00d97e" : "#059669", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>✏️ Bullet Point Rewrites</div>
                <p style={{ fontSize: "12px", color: t.text3, marginBottom: "14px", lineHeight: 1.6 }}>Copy these improved bullets directly into your resume:</p>
                {result.bullet_rewrites.map((rw, i) => (
                  <div key={i} style={{ marginBottom: i < result.bullet_rewrites.length - 1 ? "16px" : 0, paddingBottom: i < result.bullet_rewrites.length - 1 ? "16px" : 0, borderBottom: i < result.bullet_rewrites.length - 1 ? `1px solid ${t.fixRowBorder}` : "none" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: dark ? "#ff4d6d" : "#dc2626", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>Before</div>
                    <div style={{ fontSize: "12px", color: t.text3, fontStyle: "italic", background: t.bg3, borderRadius: "7px", padding: "8px 12px", marginBottom: "8px", lineHeight: 1.6 }}>{rw.original}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: dark ? "#00d97e" : "#059669", textTransform: "uppercase", letterSpacing: "0.8px" }}>After</div>
                      <button 
                        onClick={() => copyToClipboard(rw.improved, `bullet-${i}`)}
                        style={{ background: "none", border: "none", color: accent, cursor: "pointer", fontSize: "10px", fontWeight: 700, padding: 0 }}
                      >
                        {copiedId === `bullet-${i}` ? "✓ COPIED" : "📋 COPY"}
                      </button>
                    </div>
                    <div style={{ fontSize: "12px", color: t.text2, background: dark ? "rgba(0,217,126,0.07)" : "rgba(5,150,105,0.05)", border: `1px solid ${dark ? "rgba(0,217,126,0.2)" : "rgba(5,150,105,0.15)"}`, borderRadius: "7px", padding: "8px 12px", lineHeight: 1.6 }}>{rw.improved}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="card" style={{ marginBottom: "24px", border: `1.5px solid ${accentBorder}`, background: accentLight, position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.8px" }}>✨ Optimised Professional Summary</div>
                <button 
                  onClick={() => copyToClipboard(result.rewrite_summary, 'summary')}
                  style={{ background: "none", border: "none", color: accent, cursor: "pointer", fontSize: "12px", fontWeight: 700, padding: 0 }}
                >
                  {copiedId === 'summary' ? "✓ COPIED" : "📋 COPY"}
                </button>
              </div>
              <p style={{ fontSize: "14px", color: t.text2, lineHeight: 1.8, fontStyle: "italic" }}>"{result.rewrite_summary}"</p>
              <div style={{ fontSize: "11px", color: t.text4, marginTop: "8px" }}>Copy this into your resume's summary section for better ATS ranking</div>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {credits > 0
                ? <button className="btn-primary" onClick={analyzeAnother} style={{ flex: 1, minWidth: "160px" }}>Analyze Another Resume ({credits} left)</button>
                : <button className="btn-primary" onClick={changePlan} style={{ flex: 1, minWidth: "160px" }}>Buy More Credits</button>}
              <button className="btn-ghost" onClick={handleSavePDF}>💾 Save as PDF</button>
              <button className="btn-ghost" onClick={changePlan}>View Plans</button>
            </div>
            <div style={{ textAlign: "center", marginTop: "12px", fontSize: "12px", color: t.text4 }}>
              Results emailed to you automatically · Credits valid for 12 months of activity
            </div>

          </div>
        )}
      </div>
      <Footer t={t} goPage={goPage} />
    </div>
  );
}
